import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { RequireCapability } from "@/components/RequireCapability";
import { useAuth } from "@/lib/auth/auth-context";
import { isLocalPartnerOnly } from "@/lib/auth/roles";

import { useGroupsQuery } from "../api";
import { InvitationAccept } from "./InvitationAccept";
import { MemberDataView } from "./MemberDataView";
import type { MemberTab } from "./MemberTable";
import { PartnerGroupDetail } from "./PartnerGroupDetail";
import { PartnerGroupList } from "./PartnerGroupList";

export interface ViewingMember {
  profileId: string;
  name: string;
  tab?: MemberTab;
}

/**
 * Top-level view for the `/partner-group` route (frozen route tree). Composes:
 *  - the partner-group list ⇄ detail (members, license pool, invitations),
 *  - admin-only regional-partner (super-user) management, and
 *  - invitation acceptance, deep-linked via `?accept=<token>`.
 * View selection is local state — the slice owns one route.
 *
 * The `?accept=<token>` branch is intentionally NOT behind the `partnerGroups`
 * gate: an invitee following the emailed link does not yet hold that capability
 * (accepting is what grants it). Only the management surface is gated.
 */
export function PartnersApp() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const search = useSearch({ strict: false }) as { accept?: unknown };
  const acceptToken = typeof search.accept === "string" ? search.accept : "";

  const [groupId, setGroupId] = useState<string | null>(null);
  const [member, setMember] = useState<ViewingMember | null>(null);

  // A local-partner-only user can't create groups, so a one-item list is
  // pointless — open their sole group directly (mirrors the old app's
  // PartnerGroupListView.get redirect). No list means no "back", so the detail
  // hides its back control in this mode.
  const groups = useGroupsQuery();
  const soleGroupId =
    isLocalPartnerOnly(user) && groups.data?.length === 1
      ? (groups.data[0]?.id ?? null)
      : null;
  const effectiveGroupId = groupId ?? soleGroupId;

  const clearAccept = () =>
    void navigate({ to: "/partner-group", search: {}, replace: true });

  if (acceptToken) {
    return <InvitationAccept token={acceptToken} onDone={clearAccept} />;
  }

  if (member) {
    return (
      <RequireCapability required={["partnerGroups"]}>
        <MemberDataView
          profileId={member.profileId}
          memberName={member.name}
          initialTab={member.tab}
          onBack={() => setMember(null)}
        />
      </RequireCapability>
    );
  }

  return (
    <RequireCapability required={["partnerGroups"]}>
      <div className="space-y-6">
        {effectiveGroupId == null ? (
          <PartnerGroupList onOpen={setGroupId} />
        ) : (
          <PartnerGroupDetail
            groupId={effectiveGroupId}
            onClose={soleGroupId ? undefined : () => setGroupId(null)}
            onViewMember={(profileId, name) => setMember({ profileId, name })}
          />
        )}
      </div>
    </RequireCapability>
  );
}
